/**
 * List groups tab — which groups this container consumes, in what order.
 * Groups are reusable entities from /admin/list-groups; chips and match
 * counts join client-side from the already-cached groups query.
 * The list verdict, A list fails and scaling sit here too — the same cards
 * as Verdicts (20/10/2026 — user: "a list fails should be inside list
 * verdicts please").
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';

import { SearchableSelect } from '@/components/ui';
import { type ListGroup, useListGroups } from '@/features/list-groups';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import type { ContainerEditor } from '../state/useContainerEditor';

import { LinkedGroupCard } from './LinkedGroupCard';
import { useGroupDays } from './lists-per-day/use-group-days';
import { useRepick } from './repick/use-repick';
import { ListGroupDialog } from './ListGroupDialog';
import { GroupVerdicts } from './verdicts';

interface Props {
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  countryName: string;
  containerId: string | null;
}

export const GroupsTab: FC<Props> = ({ editor, plan, countryName, containerId }) => {
  const countryKey = plan.countryKey;
  /** The list group pop-up: null = closed; `group: null` = a new one. */
  const [dialog, setDialog] = useState<{ group: ListGroup | null } | null>(null);
  /** 23/10/2026 — each group card folds on its own; all start folded. */
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());
  const setOpen = (id: string, open: boolean) =>
    setOpenIds((ids) => {
      const next = new Set(ids);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  /** The group whose list formula window is open — Formula opens it at once (user: "it should open automatically"). */
  const [formulaFor, setFormulaFor] = useState<string | null>(null);
  const groups = useListGroups(countryKey);
  const { draft, groupAdd } = editor;
  const days = useGroupDays(draft, plan, containerId); // the Today grid on each card (19/10/2026)
  const repick = useRepick(countryKey, containerId); // 21/10/2026 — Re-pick the list on each card

  const byId = useMemo(
    () => new Map((groups.data ?? []).map((g) => [g.id, g])),
    [groups.data],
  );
  const linkedIds = new Set(draft.groups.map((g) => g.listGroupId));
  const addOptions = (groups.data ?? [])
    .filter((g) => !linkedIds.has(g.id) && g.scope === 'SHARED')
    .map((g) => ({
      value: g.id,
      label: `${g.name}${g.matchCount != null ? ` · ${g.matchCount} lists` : ''}`,
    }));

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        The container consumes these groups top-to-bottom. A group is defined
        once and shared — edit its rule here and every container using it
        follows; how its drops use its lists is set on its card.
      </p>
      {draft.groups.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-background px-4 py-5 text-sm text-muted-foreground">
          No groups linked — the container has no lists to consume. Add an
          existing group below, or create a new one.
        </p>
      ) : (
        draft.groups.map((stored, index) => (
          <LinkedGroupCard
            key={stored.listGroupId}
            stored={stored}
            day={days.get(stored.listGroupId)}
            group={byId.get(stored.listGroupId)}
            index={index}
            count={draft.groups.length}
            editor={editor}
            formulaSource={{ countryKey, campaignWiserId: draft.campaignWiserId }}
            open={openIds.has(stored.listGroupId)}
            formulaOpen={formulaFor === stored.listGroupId}
            onOpen={(open) => setOpen(stored.listGroupId, open)}
            onFormulaOpen={(open) => setFormulaFor(open ? stored.listGroupId : null)}
            onEdit={byId.has(stored.listGroupId) ? () => setDialog({ group: byId.get(stored.listGroupId) ?? null }) : undefined}
            onRepick={containerId ? () => void repick.repick(stored.listGroupId) : undefined}
            repicking={repick.pending}
          />
        ))
      )}
      <div className="flex flex-wrap items-center gap-3">
        {addOptions.length > 0 ? (
          <div className="w-80">
            <SearchableSelect
              options={addOptions}
              value=""
              onChange={(id) => groupAdd(id)}
              placeholder="+ Add a group…"
              searchPlaceholder="Search groups…"
            />
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">
            {(groups.data ?? []).length === 0
              ? 'This country has no groups yet.'
              : 'Every shared group of this country is already linked.'}
          </span>
        )}
        <button
          type="button"
          onClick={() => setDialog({ group: null })}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          <Plus className="h-3 w-3" /> New group
        </button>
      </div>
      {editor.draft.kind === 'ROUTE_TEST' ? null : (
        <GroupVerdicts editor={editor} plan={plan} containerId={containerId} />
      )}
      <ListGroupDialog
        open={dialog !== null}
        group={dialog?.group ?? null}
        countryKey={countryKey}
        countryName={countryName}
        onClose={() => setDialog(null)}
        onCreated={(created) => groupAdd(created.id)}
      />
    </div>
  );
};
